import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { ledgerBookList } from "../../../../../redux/actions/action-creator";
import LedgerBookFooter from "../../../../../components/Footer/LedgerBookFooter";
import Form from "../../../../../components/Form/Form";
import { useDropdownFilter } from "../../../../../components/Custom_Hooks/useDropdownFilter";
import { useDropdownScroll } from "../../../../../components/Custom_Hooks/useDropdownScroll";
import { useDropdownKeyboardNavigation } from "../../../../../components/Custom_Hooks/useDropdownKeyboardNavigation";
import { HighlightedText } from "../../../../../components/HighLightText/HighlightedText";

// Constants for table columns
const TABLE_COLUMNS = {
  ACCOUNT: { width: "5%",col: "1", label: "A/c" },
  LEDGER_NAME: { width: "33%",col: "3", label: "Ledger Name" },
  GROUP: { width: "15%",col: "2", label: "Group" },
  CITY: { width: "15%",col: "2", label: "City" },
  MOBILE: { width: "8%",col: "1", label: "Mobile No" },
  AADHAAR: { width: "11%",col: "2", label: "Aadhaar No" },
  CLOSING_BAL: { width: "13%",col: "1", label: "Closing Bal." }
};
const LedgerBook = (props) => {
 
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const [load, setLoad] = useState(false);
  const [allData, setAllData] = useState([]);
  const [inputValue, setInputValue] = useState({ input_name: "" });

  const { userData = null } = useSelector((state) => state.auth);

  // Memoized filtered data
  const { filteredData } = useDropdownFilter({
    dataList: allData,
    searchInput: inputValue?.input_name,
    inputRef: inputRef,
  });

  // Handle input change with debouncing
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setInputValue(prev => ({ ...prev, [name]: value }));
  }, []);

  // Handle item selection
  const handleItemSelect = useCallback((item) => {
    navigate(`../ledgervoucher/${item?.id}`);
  }, [navigate]);

  const handleItemCtrlA = useCallback((item) => {
    navigate(`../ledgervoucher/${item?.id}`);
  }, [navigate]);

  // Keyboard navigation setup
  const { selectedItemIndex, setSelectedItemIndex } = useDropdownKeyboardNavigation({
    items: filteredData,
    state: true,
    onItemSelect: handleItemSelect,
    onItemCtrlASelect: handleItemCtrlA,
    input_value: inputValue?.input_name,
  });

  useDropdownScroll({ dropdownRef, selectedItemIndex });

  // Fetch ledger book data
  useEffect(() => {
    const fetchData = async () => {
      if (!userData?.id) return navigate('/');
      
      setLoad(true);
      try {
        const data = new FormData();
        data.append("company_id", userData.id);
        
        const response = await dispatch(ledgerBookList(data));
        if (response.status) {
          setAllData(response.data);
        }
      } catch (error) {
        console.error("Error fetching ledger book data:", error);
      } finally {
        setLoad(false);
      }
    };

    fetchData();
  }, [dispatch, userData?.id]);

  // Memoized table header
  const tableHeader = useMemo(() => (
    <div className="col-12 table_header_font table_date_header">
      <div className="row px-1">
        {Object.entries(TABLE_COLUMNS).map(([key, { width, col, label }]) => (
          <div
            key={key}
            className={`col-${col} ${key === 'ACCOUNT' || key === 'CLOSING_BAL' ? 'text-end' : 'text-start'}`}
            style={{ width }}
          >
            {label}
          </div>
        ))}
      </div>
    </div>
  ), []);

  // Create a wrapper component for the footer with props
  const FooterWithProps = () => (
    <LedgerBookFooter 
      Accept={() => {
        navigate(`../ledgervoucher/${filteredData[selectedItemIndex]?.id}`);
      }}
    />
  );

  return (
    <Form
      title="Select Ledger"
      loading={load}
      col="col-12"
      Footer={FooterWithProps}
      closeModal_sec={()=>{navigate('/accountbook')}}
    >
      <div className="col-12 px-1" style={{height:'4.5vh'}}>
        <input
          autoFocus
          autoComplete="off"
          className="voucher_entry_account_input"
          type="text"
          id="input_name"
          name="input_name"
          style={{ margin: 0, padding: 0, paddingLeft: "5px" }}
          onKeyUp={handleChange}
          onChange={handleChange}
          ref={inputRef}
          value={inputValue?.input_name || ""}
          placeholder="Search by name, number, mobile, aadhaar, balance..."
        />
      </div>
      {tableHeader}
      <div
        className="col-12 m-0 p-0"
        style={{ 
          height: "78vh", 
          overflowY: "auto", 
          overflowX: 'hidden',
        }}
        ref={dropdownRef}
      >
        {filteredData?.length > 0 ? (
          filteredData.map((item, index) => (
            <div
              className={`table_header_font dropdown_link ${
                index === selectedItemIndex ? "selected" : ""
              }`}
              key={item.id || index}
              onClick={(e) => {
                e.preventDefault();
                setSelectedItemIndex(index);
                if(index === selectedItemIndex){
                  navigate(`../ledgervoucher/${item?.id}`);
                }
              }}
              onDoubleClick={() => {
                navigate(`../ledgervoucher/${item?.id}`);
              }}
            >
                <div className="row px-1 mt-0">
                  <div className="col-1 text-end" style={{ width: TABLE_COLUMNS.ACCOUNT.width }}>
                    <HighlightedText 
                      text={item?.id || "55559"} 
                      searchTerm={inputValue?.input_name} 
                    />
                  </div>
                  <div className="col-3 text-start" style={{ width: TABLE_COLUMNS.LEDGER_NAME.width }}>
                    <HighlightedText 
                      text={item?.ledger_name} 
                      searchTerm={inputValue?.input_name} 
                    />
                  </div>
                  <div className="col-2 text-start" style={{ width: TABLE_COLUMNS.GROUP.width }}>
                    <HighlightedText 
                      text={item?.under_name} 
                      searchTerm={inputValue?.input_name} 
                    />
                  </div>
                  <div className="col-2 text-start" style={{ width: TABLE_COLUMNS.CITY.width }}>
                    <HighlightedText 
                      text={item?.city} 
                      searchTerm={inputValue?.input_name} 
                    />
                  </div>
                  <div className="col-1 text-start" style={{ width: TABLE_COLUMNS.MOBILE.width }}>
                    <HighlightedText 
                      text={item?.mobile_no} 
                      searchTerm={inputValue?.input_name} 
                    />
                  </div>
                  <div className="col-2 text-start" style={{ width: TABLE_COLUMNS.AADHAAR.width }}>
                    <HighlightedText 
                      text={item?.aadhaar_no} 
                      searchTerm={inputValue?.input_name} 
                    />
                  </div>
                  <div className="col-1 text-end" style={{ width: TABLE_COLUMNS.CLOSING_BAL.width }}>
                    <HighlightedText 
                      text={item?.opening_balance} 
                      searchTerm={inputValue?.input_name} 
                    />
                  </div>
                </div>
            </div>
          ))
        ) : (
          <p style={{ color: "red", textAlign: "center", padding: "20px" }}>
            {inputValue?.input_name ? "No matching items found" : "No data available"}
          </p>
        )}
      </div>
    </Form>
  );
}

export default React.memo(LedgerBook);
